import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { MoneyFlow, Pipeline, StatusBadge } from '../components/Ledger';
import ScreenshotFrame from '../components/ScreenshotFrame';
import { getCaseStudy } from '../data/projects';
import usePageMeta from '../hooks/usePageMeta';
import '../styles/CaseStudy.css';

// "Name (detail, detail)" -> { name, detail }
const splitSkill = (skill) => {
  const match = skill.match(/^(.+?) \((.+)\)$/);
  return match ? { name: match[1], detail: match[2] } : { name: skill, detail: null };
};

const Section = ({ id, title, wide = false, children }) => (
  <section className={`case-band${wide ? ' is-wide' : ''}`} aria-labelledby={id}>
    <h2 id={id} className="label label-accent">
      {title}
    </h2>
    <div className="case-band-body">{children}</div>
  </section>
);

const Skills = ({ skills }) => {
  const parsed = skills.map(splitSkill);
  if (parsed.every((s) => !s.detail)) {
    return (
      <ul className="chip-list">
        {parsed.map((s) => (
          <li key={s.name} className="chip">
            {s.name}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul className="skill-detail-list">
      {parsed.map((s) => (
        <li key={s.name}>
          <span className="skill-name">{s.name}</span>
          {s.detail && <span className="skill-detail">{s.detail}</span>}
        </li>
      ))}
    </ul>
  );
};

const CaseStudy = () => {
  const { slug } = useParams();
  const study = getCaseStudy(slug);
  usePageMeta(study?.title, study?.summary);

  if (!study) return <Navigate to="/projects" replace />;

  return (
    <div className="case-study">
      <header className="page-header case-header container">
        <Link to="/projects" className="label back-link">
          <span aria-hidden="true">←</span> Work
        </Link>
        <StatusBadge status={study.status} />
        <h1>{study.title}</h1>
        <p>{study.summary}</p>
      </header>

      <div className="container case-body">
        {study.role && (
          <Section id="role" title="My role">
            <p className="case-lead">{study.role}</p>
          </Section>
        )}

        <Section id="architecture" title="Architecture" wide={study.architectureFeatured}>
          <Pipeline steps={study.architecture} featured={study.architectureFeatured} label="Architecture" />
          {study.architectureNote && <p className="architecture-note">{study.architectureNote}</p>}
          {study.moneyFlow && <MoneyFlow steps={study.moneyFlow} />}
        </Section>

        <Section id="screenshots" title="Screenshots">
          <div className="screenshot-gallery">
            {study.screenshots.map((shot) => (
              <ScreenshotFrame key={shot.file} slug={study.slug} screenshot={shot} />
            ))}
          </div>
        </Section>

        <Section id="stack" title="Tech stack">
          <div className="stack-groups">
            {study.stack.map((group) => (
              <div key={group.group} className="stack-group">
                <h3>{group.group}</h3>
                <ul className="chip-list">
                  {group.items.map((item) => (
                    <li key={item} className="chip">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {study.built && (
          <Section id="built" title="What I built / designed">
            <div className="built-groups">
              {study.built.map((group) => (
                <div key={group.heading} className="built-group">
                  <h3>{group.heading}</h3>
                  <ul className={group.items.some((i) => i.title) ? 'built-list' : 'built-list is-compact'}>
                    {group.items.map((item) => (
                      <li key={item.title || item.text}>
                        {item.title && <span className="built-title">{item.title}</span>}
                        <span className="built-text">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>
        )}

        {study.results && (
          <Section id="results" title={study.results.heading}>
            <ul className="results-list">
              {study.results.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            {study.results.note && <p className="results-note">{study.results.note}</p>}
          </Section>
        )}

        <Section id="skills" title="Skills demonstrated">
          <Skills skills={study.skills} />
        </Section>
      </div>
    </div>
  );
};

export default CaseStudy;
